import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Layout from '../components/Layout';
import {
  Zap, Plus, Trash2, Send, X, Calendar, Target, ToggleLeft, ToggleRight
} from 'lucide-react';

// Les niveaux de fidélité sont des valeurs backend (ne pas traduire la valeur envoyée au serveur).
// Mapping séparé vers des clés i18n uniquement pour l'affichage, repli sur la valeur brute.
const LEVEL_I18N = { Bronze: 'flashOffers.levelBronze', Argent: 'flashOffers.levelSilver', Or: 'flashOffers.levelGold' };

const FlashOffersPage = () => {
  const { api, isAdmin } = useAuth();
  const { t, formatDate } = useLanguage();
  const levelLabel = (lvl) => (LEVEL_I18N[lvl] ? t(LEVEL_I18N[lvl]) : lvl);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', discount_percent: 10, end_date: '', target_levels: ['Or', 'Argent'] });
  const [formLoading, setFormLoading] = useState(false);

  const fetchOffers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/flash-offers');
      setOffers(res.data || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [api]);

  useEffect(() => { fetchOffers(); }, [fetchOffers]);

  const handleCreate = async () => {
    if (!form.title.trim()) return;
    setFormLoading(true);
    try {
      await api.post('/flash-offers', form);
      setShowForm(false);
      setForm({ title: '', description: '', discount_percent: 10, end_date: '', target_levels: ['Or', 'Argent'] });
      await fetchOffers();
    } catch (err) { console.error(err); }
    setFormLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('flashOffers.confirmDelete'))) return;
    try { await api.delete(`/flash-offers/${id}`); await fetchOffers(); } catch (err) { console.error(err); }
  };

  const handleToggle = async (offer) => {
    try { await api.put(`/flash-offers/${offer.id}`, { active: !offer.active }); await fetchOffers(); } catch (err) { console.error(err); }
  };

  const handleNotify = async (id) => {
    try {
      const res = await api.post(`/flash-offers/${id}/notify`);
      alert(res.data.message || t('flashOffers.emailsSentFallback'));
      await fetchOffers();
    } catch (err) { console.error(err); }
  };

  const toggleLevel = (level) => {
    const levels = form.target_levels.includes(level) ? form.target_levels.filter(l => l !== level) : [...form.target_levels, level];
    setForm({ ...form, target_levels: levels });
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="flash-offers-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-500 flex items-center gap-2">
              <Zap className="w-6 h-6 text-amber-400" /> {t('flashOffers.pageTitle')}
            </h1>
            <p className="text-sm text-gray-400 mt-1">{t('flashOffers.pageSubtitle')}</p>
          </div>
          {isAdmin && (
            <button onClick={() => setShowForm(!showForm)} className="px-4 py-2 rounded-xl bg-amber-500 text-black font-semibold text-sm flex items-center gap-2 hover:bg-amber-400 transition-colors" data-testid="new-flash-offer-btn">
              <Plus className="w-4 h-4" /> {t('flashOffers.newOffer')}
            </button>
          )}
        </div>

        {/* Create Form */}
        {showForm && (
          <div className="glass p-6 rounded-xl border border-amber-500/30 space-y-4" data-testid="flash-offer-create-form">
            <div className="flex justify-between items-center">
              <h3 className="font-bold text-amber-400">{t('flashOffers.createTitle')}</h3>
              <button onClick={() => setShowForm(false)}><X className="w-4 h-4 text-gray-400 hover:text-white" /></button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-400 mb-1">{t('flashOffers.labelTitle')}</label>
                <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="input-dark w-full" placeholder={t('flashOffers.placeholderTitle')} data-testid="flash-offer-title" />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">{t('flashOffers.labelDiscount')}</label>
                <input type="number" value={form.discount_percent} onChange={(e) => setForm({ ...form, discount_percent: parseInt(e.target.value) || 0 })}
                  min="1" max="90" className="input-dark w-full" data-testid="flash-offer-discount" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs text-gray-400 mb-1">{t('flashOffers.labelDescription')}</label>
                <input type="text" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="input-dark w-full" placeholder={t('flashOffers.placeholderDescription')} data-testid="flash-offer-desc" />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">{t('flashOffers.labelEndDate')}</label>
                <input type="datetime-local" value={form.end_date ? form.end_date.slice(0, 16) : ''}
                  onChange={(e) => setForm({ ...form, end_date: e.target.value ? new Date(e.target.value).toISOString() : '' })}
                  className="input-dark w-full" data-testid="flash-offer-end" />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">{t('flashOffers.labelTargetLevels')}</label>
                <div className="flex gap-2">
                  {['Bronze', 'Argent', 'Or'].map(level => (
                    <button key={level} type="button" onClick={() => toggleLevel(level)}
                      className={`px-3 py-2 rounded-lg text-xs font-semibold transition-colors ${
                        form.target_levels.includes(level)
                          ? 'bg-amber-500/30 text-amber-400 border border-amber-500/40'
                          : 'bg-white/5 text-gray-500 border border-white/10'
                      }`}>
                      {levelLabel(level)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <button onClick={handleCreate} disabled={formLoading || !form.title.trim()}
              className="w-full py-2.5 rounded-xl bg-amber-500 text-black font-bold text-sm hover:bg-amber-400 transition-colors disabled:opacity-50"
              data-testid="flash-offer-submit">
              {formLoading ? t('flashOffers.creating') : t('flashOffers.createOffer')}
            </button>
          </div>
        )}

        {/* Offers List */}
        {loading ? (
          <div className="flex justify-center py-12"><div className="loading-spinner"></div></div>
        ) : offers.length === 0 ? (
          <div className="glass p-8 text-center rounded-xl">
            <Zap className="w-12 h-12 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400">{t('flashOffers.emptyTitle')}</p>
            <p className="text-xs text-gray-600 mt-1">{t('flashOffers.emptyHint', { btn: t('flashOffers.newOffer') })}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {offers.map(o => {
              const isActive = o.active;
              const endDate = o.end_date ? new Date(o.end_date) : null;
              const isExpired = endDate && endDate < new Date();
              return (
                <div key={o.id} className={`glass p-4 rounded-xl border ${isActive && !isExpired ? 'border-amber-500/30' : 'border-white/5 opacity-60'}`} data-testid={`flash-offer-card-${o.id}`}>
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 rounded-xl bg-amber-500/20 flex flex-col items-center justify-center shrink-0">
                      <span className="text-amber-400 font-black text-xl">-{o.discount_percent}%</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-bold text-white text-lg">{o.title}</h3>
                        {isExpired && <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/20 text-red-400 border border-red-500/30">{t('flashOffers.badgeExpired')}</span>}
                        {!isActive && <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-500/20 text-gray-400 border border-gray-500/30">{t('flashOffers.badgeDisabled')}</span>}
                      </div>
                      {o.description && <p className="text-sm text-gray-400 mt-1">{o.description}</p>}
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-500 flex-wrap">
                        <span className="flex items-center gap-1"><Target className="w-3 h-3" /> {(o.target_levels || []).map(levelLabel).join(', ')}</span>
                        {endDate && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {t('flashOffers.endLabel', { date: formatDate(endDate, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) })}</span>}
                        <span>{t('flashOffers.emailsCount', { count: o.emails_sent || 0 })}</span>
                        {o.last_notified_at && <span>{t('flashOffers.lastSent', { date: formatDate(new Date(o.last_notified_at)) })}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button onClick={() => handleToggle(o)} className={`p-2 rounded-lg ${isActive ? 'bg-emerald-500/20 text-emerald-400' : 'bg-white/10 text-gray-500'}`}
                        title={isActive ? t('flashOffers.deactivate') : t('flashOffers.activate')} data-testid={`toggle-flash-${o.id}`}>
                        {isActive ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                      </button>
                      <button onClick={() => handleNotify(o.id)} className="p-2 rounded-lg bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30"
                        title={t('flashOffers.sendByEmail')} data-testid={`notify-flash-${o.id}`}>
                        <Send className="w-5 h-5" />
                      </button>
                      <button onClick={() => handleDelete(o.id)} className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30"
                        title={t('flashOffers.delete')} data-testid={`delete-flash-${o.id}`}>
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default FlashOffersPage;
